/**
 * One report in the list, in one of three states: at rest, being renamed, or awaiting
 * delete confirmation.
 *
 * The row owns only its rename draft. Which row is renaming or confirming is coordination
 * between rows, so the list holds that — otherwise several could be mid-edit at once.
 */

import { useState } from 'react';
import {
    Button,
    IconButton,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';

import type { SummaryReport } from '../../types.ts';

/**
 * Rows are for reading: loading is an explicit Open, and the edit actions stay invisible
 * until hover or focus. Opacity rather than display, so they keep their place in the tab
 * order.
 */
const ROW_SX = {
    // Clearance for the action cluster. MUI's default secondaryAction padding assumes one
    // icon, and the text would run under ours.
    pr: 23,
    '& .row-actions': {
        opacity: 0,
        transition: 'opacity 120ms',
    },
    '&:hover .row-actions, &:focus-within .row-actions': {
        opacity: 1,
    },
};

/** Second line of a row: what the report covers, when it was made, who can see it. */
function describeReport(report: SummaryReport): string {
    const parts: string[] = [];
    if (report.description) parts.push(report.description);
    parts.push(new Date(report.createdAt).toLocaleDateString());

    if (report.visibility === 'shared') {
        const count = report.sharedWithUserIds?.length ?? 0;
        parts.push(count === 1 ? 'shared with 1 person' : `shared with ${count} people`);
    } else if (report.visibility === 'org') {
        parts.push('visible to organization');
    } else {
        parts.push('private');
    }

    return parts.join(' · ');
}

// ─── Rename ──────────────────────────────────────────────────────────────────

/**
 * Split out so the draft seeds from `useState` on mount rather than an effect watching the
 * row — this mounts only once renaming starts, and unmounting discards the draft.
 */
function RenameRow({
    report,
    onCommit,
    onCancel,
}: {
    report: SummaryReport;
    onCommit: (report: SummaryReport, name: string, description?: string) => void;
    onCancel: () => void;
}) {
    const [name, setName] = useState(report.name);
    const [description, setDescription] = useState(report.description ?? '');

    return (
        <ListItem sx={{ display: 'block' }}>
            <Stack spacing={1} sx={{ py: 1 }}>
                <TextField
                    size='small'
                    label='Report Name'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    fullWidth
                />
                <TextField
                    size='small'
                    label='Description (optional)'
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                />
                <Stack direction='row' spacing={1}>
                    <Button
                        size='small'
                        variant='contained'
                        disabled={!name.trim()}
                        onClick={() =>
                            onCommit(report, name.trim(), description.trim() || undefined)
                        }
                    >
                        Save Name
                    </Button>
                    <Button size='small' onClick={onCancel}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </ListItem>
    );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SummaryReportRowProps {
    report: SummaryReport;
    /** Only the owner may rename or delete. */
    isOwned: boolean;
    isRenaming: boolean;
    isConfirmingDelete: boolean;

    onOpen: (report: SummaryReport) => void;
    onStartRename: (report: SummaryReport) => void;
    onCommitRename: (report: SummaryReport, name: string, description?: string) => void;
    onCancelRename: () => void;
    onStartDelete: (report: SummaryReport) => void;
    onConfirmDelete: (report: SummaryReport) => void;
    onCancelDelete: () => void;
    onShare: (report: SummaryReport) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SummaryReportsDialogRow({
    report,
    isOwned,
    isRenaming,
    isConfirmingDelete,
    onOpen,
    onStartRename,
    onCommitRename,
    onCancelRename,
    onStartDelete,
    onConfirmDelete,
    onCancelDelete,
    onShare,
}: SummaryReportRowProps) {
    if (isRenaming) {
        return <RenameRow report={report} onCommit={onCommitRename} onCancel={onCancelRename} />;
    }

    if (isConfirmingDelete) {
        return (
            <ListItem sx={{ display: 'block' }}>
                <Stack spacing={1} sx={{ py: 1 }}>
                    <Typography variant='body2'>
                        Delete <strong>{report.name}</strong>? This can't be undone.
                    </Typography>
                    <Stack direction='row' spacing={1}>
                        <Button
                            size='small'
                            variant='contained'
                            color='error'
                            onClick={() => onConfirmDelete(report)}
                        >
                            Delete
                        </Button>
                        <Button size='small' onClick={onCancelDelete}>
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            </ListItem>
        );
    }

    return (
        <ListItem
            sx={ROW_SX}
            secondaryAction={
                <Stack direction='row' spacing={0.5} alignItems='center'>
                    {isOwned && (
                        // At zero opacity rather than unmounted, so they stay tabbable.
                        // ROW_SX reveals them on hover and focus.
                        <Stack direction='row' spacing={0.5} className='row-actions'>
                            <Tooltip title='Rename'>
                                <IconButton
                                    size='small'
                                    aria-label={`rename ${report.name}`}
                                    onClick={() => onStartRename(report)}
                                >
                                    <EditIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Share'>
                                <IconButton
                                    size='small'
                                    aria-label={`share ${report.name}`}
                                    onClick={() => onShare(report)}
                                >
                                    <ShareIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Delete'>
                                <IconButton
                                    size='small'
                                    aria-label={`delete ${report.name}`}
                                    onClick={() => onStartDelete(report)}
                                >
                                    <DeleteIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                    <Button
                        size='small'
                        variant='outlined'
                        aria-label={`open ${report.name}`}
                        onClick={() => onOpen(report)}
                    >
                        Open
                    </Button>
                </Stack>
            }
        >
            <ListItemText primary={report.name} secondary={describeReport(report)} />
        </ListItem>
    );
}
